import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
const SCREENSHOT_ANALYTICS = '/images/screenshot_analytics.png?v=4';
const SCREENSHOT_SCORE = '/images/screenshot_score.png?v=4';
const SCREENSHOT_HEALING = '/images/screenshot_healing.png?v=4';


/* ─────────────────────────────────────────────────────────────────────────────
   RDRTO — Landing Page
   Dark Navy Enterprise Design · Pure CSS Animations · No extra libraries
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .rdrto-page {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    background: #0a0f1e;
    color: #e2e8f0;
    overflow-x: hidden;
  }

  /* ── Hero ── */
  .hero {
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: radial-gradient(ellipse at 60% 40%, #1e1060 0%, #0a0f1e 60%),
                radial-gradient(ellipse at 20% 80%, #0d2060 0%, transparent 50%);
  }

  .hero-canvas {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  .hero-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: particleFade 8s ease-in-out infinite;
  }

  @keyframes particleFade {
    0%   { opacity: 0; transform: translateY(0) scale(1); }
    20%  { opacity: 0.6; }
    80%  { opacity: 0.3; }
    100% { opacity: 0; transform: translateY(-120px) scale(0.5); }
  }

  .hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
    padding: 80px 24px 60px;
    max-width: 900px;
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.4);
    color: #a5b4fc;
    padding: 6px 18px;
    border-radius: 100px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 32px;
    animation: fadeInDown 0.7s ease both;
  }

  .hero-badge-dot {
    width: 6px; height: 6px;
    background: #6366f1;
    border-radius: 50%;
    animation: pulse 1.8s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50%       { transform: scale(1.5); opacity: 0.5; }
  }

  .hero-brand {
    font-size: clamp(4rem, 12vw, 9rem);
    font-weight: 900;
    letter-spacing: -0.04em;
    line-height: 1;
    margin-bottom: 12px;
    animation: fadeInUp 0.8s ease 0.1s both;
    background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 40px rgba(99,102,241,0.4));
  }

  .hero-tagline-main {
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    font-weight: 500;
    color: #94a3b8;
    margin-bottom: 28px;
    animation: fadeInUp 0.8s ease 0.2s both;
    letter-spacing: 0.03em;
  }

  .hero-tagline-main strong {
    color: #c7d2fe;
  }

  .hero-description {
    font-size: clamp(1rem, 2vw, 1.15rem);
    line-height: 1.75;
    color: #64748b;
    max-width: 640px;
    margin: 0 auto 44px;
    animation: fadeInUp 0.8s ease 0.3s both;
  }

  .hero-ctas {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
    animation: fadeInUp 0.8s ease 0.4s both;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 16px 36px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.25s ease;
    box-shadow: 0 4px 24px rgba(99, 102, 241, 0.4);
    letter-spacing: 0.01em;
  }
  .btn-primary:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 32px rgba(99, 102, 241, 0.6);
    background: linear-gradient(135deg, #818cf8, #6366f1);
  }

  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 16px 36px;
    background: transparent;
    color: #c7d2fe;
    border: 1.5px solid rgba(99, 102, 241, 0.5);
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.25s ease;
    letter-spacing: 0.01em;
  }
  .btn-secondary:hover {
    border-color: #6366f1;
    background: rgba(99, 102, 241, 0.12);
    transform: translateY(-2px);
  }

  .hero-scroll-hint {
    position: absolute;
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #475569;
    font-size: 12px;
    animation: fadeIn 1.5s ease 1.5s both;
  }

  .scroll-arrow {
    width: 20px; height: 20px;
    border-right: 2px solid #475569;
    border-bottom: 2px solid #475569;
    transform: rotate(45deg);
    animation: bounce 1.4s ease-in-out infinite;
  }

  @keyframes bounce {
    0%, 100% { transform: rotate(45deg) translateY(0); }
    50%       { transform: rotate(45deg) translateY(5px); }
  }

  /* ── Trust Bar ── */
  .trust-bar {
    background: rgba(15, 23, 42, 0.9);
    border-top: 1px solid rgba(99, 102, 241, 0.15);
    border-bottom: 1px solid rgba(99, 102, 241, 0.15);
    padding: 28px 24px;
  }

  .trust-bar-inner {
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
  }

  .trust-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .trust-stat-value {
    font-size: 1.6rem;
    font-weight: 800;
    color: #a5b4fc;
    line-height: 1;
  }

  .trust-stat-label {
    font-size: 0.78rem;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.07em;
  }

  .trust-divider {
    width: 1px;
    height: 36px;
    background: rgba(99, 102, 241, 0.2);
  }

  /* ── Section base ── */
  .section {
    padding: 100px 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-label {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #6366f1;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    line-height: 1.1;
    color: #f1f5f9;
    margin-bottom: 20px;
    letter-spacing: -0.02em;
  }

  .section-title span {
    background: linear-gradient(135deg, #a5b4fc, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .section-sub {
    font-size: 1.05rem;
    line-height: 1.75;
    color: #64748b;
    max-width: 580px;
  }

  /* ── Brand Decoder ── */
  .brand-section {
    padding: 100px 24px;
    background: linear-gradient(180deg, #0a0f1e 0%, #0d1330 50%, #0a0f1e 100%);
  }

  .brand-inner {
    max-width: 1100px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
  }

  @media (max-width: 768px) {
    .brand-inner { grid-template-columns: 1fr; gap: 40px; }
    .hero-brand { font-size: 5rem; }
  }

  .brand-letters {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .brand-letter-row {
    display: flex;
    align-items: baseline;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(99, 102, 241, 0.12);
    animation: slideInLeft 0.6s ease both;
  }

  .brand-letter-row:last-child { border-bottom: none; }

  .brand-letter-char {
    font-size: 3.5rem;
    font-weight: 900;
    line-height: 1;
    min-width: 60px;
    text-align: center;
    background: linear-gradient(135deg, #a5b4fc, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 12px rgba(99,102,241,0.6));
  }

  .brand-letter-expand {
    font-size: 1.1rem;
    color: #94a3b8;
    line-height: 1.3;
  }

  .brand-letter-expand strong {
    color: #e2e8f0;
    font-weight: 700;
  }

  .brand-right {
    animation: slideInRight 0.7s ease 0.2s both;
  }

  .brand-intro-text {
    font-size: 1.1rem;
    line-height: 1.85;
    color: #64748b;
    margin-top: 20px;
  }

  .brand-intro-text strong {
    color: #a5b4fc;
    font-weight: 600;
  }

  /* ── How it works ── */
  .steps-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr auto 1fr;
    align-items: start;
    gap: 0;
    margin-top: 60px;
  }

  @media (max-width: 768px) {
    .steps-grid { grid-template-columns: 1fr; }
    .step-connector { display: none; }
  }

  .step-card {
    background: rgba(15, 23, 42, 0.7);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-radius: 20px;
    padding: 36px 28px;
    text-align: center;
    transition: all 0.3s ease;
    animation: fadeInUp 0.7s ease both;
  }

  .step-card:hover {
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-6px);
    box-shadow: 0 20px 60px rgba(99, 102, 241, 0.15);
  }

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px; height: 48px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    border-radius: 14px;
    font-size: 1.2rem;
    font-weight: 800;
    color: white;
    margin: 0 auto 20px;
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  }

  .step-icon {
    font-size: 2.2rem;
    margin-bottom: 16px;
    display: block;
  }

  .step-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 12px;
  }

  .step-desc {
    font-size: 0.92rem;
    line-height: 1.7;
    color: #64748b;
  }

  .step-connector {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    margin-top: 60px;
  }

  .connector-line {
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, rgba(99,102,241,0.4), rgba(99,102,241,0.8), rgba(99,102,241,0.4));
    position: relative;
  }

  .connector-arrow {
    color: #6366f1;
    font-size: 1.4rem;
  }

  /* ── Features Grid ── */
  .features-bg {
    background: rgba(5, 8, 20, 0.6);
    padding: 100px 24px;
  }

  .features-inner {
    max-width: 1200px;
    margin: 0 auto;
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 24px;
    margin-top: 60px;
  }

  .feature-card {
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid rgba(99, 102, 241, 0.12);
    border-radius: 20px;
    padding: 36px 32px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    animation: fadeInUp 0.7s ease both;
  }

  .feature-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #6366f1, transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .feature-card:hover {
    border-color: rgba(99, 102, 241, 0.35);
    transform: translateY(-6px);
    box-shadow: 0 24px 64px rgba(99, 102, 241, 0.12);
  }

  .feature-card:hover::before { opacity: 1; }

  .feature-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .feature-icon-wrap {
    width: 52px; height: 52px;
    background: rgba(99, 102, 241, 0.12);
    border: 1px solid rgba(99, 102, 241, 0.25);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    flex-shrink: 0;
  }

  .feature-badge {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 4px 12px;
    border-radius: 100px;
  }

  .badge-free {
    background: rgba(34, 197, 94, 0.12);
    color: #4ade80;
    border: 1px solid rgba(34, 197, 94, 0.25);
  }

  .badge-pro {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.3);
  }
    
  .badge-business {
   background: rgba(236,160,28,0.12);  
   color: #fbbf24; 
   border: 1px solid rgba(236,160,28,0.2);
  }

  .feature-card-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 12px;
  }

  .feature-card-desc {
    font-size: 0.92rem;
    line-height: 1.75;
    color: #64748b;
  }

  /* ── Why RDRTO ── */
  .why-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 24px;
    margin-top: 60px;
  }

  .why-card {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.1);
    border-radius: 20px;
    padding: 40px 28px;
    text-align: center;
    transition: all 0.3s ease;
    animation: fadeInUp 0.7s ease both;
    backdrop-filter: blur(10px);
  }

  .why-card:hover {
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(99, 102, 241, 0.1);
    background: rgba(15, 23, 42, 0.9);
  }

  .why-icon {
    font-size: 2.8rem;
    margin-bottom: 20px;
    display: block;
  }

  .why-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 12px;
  }

  .why-desc {
    font-size: 0.9rem;
    line-height: 1.7;
    color: #64748b;
  }

  /* ── Signature Features spotlight ── */
  .spotlight-section {
    padding: 100px 24px;
    background: linear-gradient(180deg, #0a0f1e 0%, #0c1228 50%, #0a0f1e 100%);
  }
  .spotlight-inner { max-width: 1200px; margin: 0 auto; }
  .spotlight-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    margin-top: 60px;
  }
  @media (max-width: 860px) { .spotlight-grid { grid-template-columns: 1fr; } }
  .spotlight-card {
    border-radius: 24px;
    padding: 48px 40px;
    position: relative;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .spotlight-card:hover { transform: translateY(-6px); }
  .spotlight-card-score {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(79,70,229,0.08) 100%);
    border: 1px solid rgba(99,102,241,0.3);
    box-shadow: 0 0 60px rgba(99,102,241,0.1);
  }
  .spotlight-card-score:hover { box-shadow: 0 20px 80px rgba(99,102,241,0.2); }
  .spotlight-card-heal {
    background: linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.06) 100%);
    border: 1px solid rgba(16,185,129,0.25);
    box-shadow: 0 0 60px rgba(16,185,129,0.08);
  }
  .spotlight-card-heal:hover { box-shadow: 0 20px 80px rgba(16,185,129,0.18); }
  .spotlight-tag {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.1em; padding: 4px 12px;
    border-radius: 100px; margin-bottom: 24px;
  }
  .spotlight-tag-score { background: rgba(99,102,241,0.2); color: #a5b4fc; border: 1px solid rgba(99,102,241,0.3); }
  .spotlight-tag-heal  { background: rgba(16,185,129,0.15); color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
  .spotlight-title {
    font-size: 1.9rem; font-weight: 800; line-height: 1.15;
    color: #f1f5f9; margin-bottom: 16px; letter-spacing: -0.02em;
  }
  .spotlight-desc {
    font-size: 1rem; line-height: 1.8; color: #64748b; margin-bottom: 28px;
  }
  .spotlight-bullets { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .spotlight-bullets li {
    display: flex; align-items: flex-start; gap: 12px;
    font-size: 0.93rem; color: #94a3b8; line-height: 1.55;
  }
  .spotlight-bullet-dot {
    width: 20px; height: 20px; border-radius: 6px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.75rem; margin-top: 1px;
  }
  .dot-score { background: rgba(99,102,241,0.25); }
  .dot-heal  { background: rgba(16,185,129,0.2); }

  /* ── Screenshot gallery ── */
  .screenshots-section {
    padding: 100px 24px;
    background: rgba(5, 8, 20, 0.7);
  }
  .screenshots-inner { max-width: 1200px; margin: 0 auto; }
  .screenshots-tabs {
    display: flex; gap: 8px; justify-content: center;
    margin: 48px 0 32px; flex-wrap: wrap;
  }
  .screenshot-tab {
    padding: 8px 20px; border-radius: 100px;
    font-size: 13px; font-weight: 600;
    border: 1.5px solid rgba(99,102,241,0.25);
    background: transparent; color: #64748b;
    cursor: pointer; font-family: inherit;
    transition: all 0.2s ease;
  }
  .screenshot-tab.active {
    background: rgba(99,102,241,0.18);
    border-color: rgba(99,102,241,0.5);
    color: #a5b4fc;
  }
  .screenshot-tab:hover:not(.active) { border-color: rgba(99,102,241,0.35); color: #94a3b8; }
  .screenshot-frame {
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(99,102,241,0.2);
    box-shadow: 0 40px 120px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
    background: #0a0f1e;
    max-width: 900px;
    margin: 0 auto;
    animation: fadeInUp 0.4s ease both;
  }
  .screenshot-frame img { width: 100%; display: block; }

  .cta-banner {
    margin: 0 24px 80px;
    max-width: 1152px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 80px;
    background: linear-gradient(135deg, #1e1060 0%, #312e81 50%, #1e1b74 100%);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 28px;
    padding: 72px 60px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .cta-banner::before {
    content: '';
    position: absolute;
    top: -50%; left: -50%;
    width: 200%; height: 200%;
    background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 60%);
    pointer-events: none;
  }

  .cta-banner-title {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 800;
    color: #f1f5f9;
    margin-bottom: 16px;
    letter-spacing: -0.02em;
    position: relative;
  }

  .cta-banner-sub {
    font-size: 1.05rem;
    color: #a5b4fc;
    margin-bottom: 40px;
    position: relative;
  }

  .cta-banner-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
    position: relative;
  }

  /* ── Shared animations ── */
  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-30px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(30px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

/* ── Particle data ──────────────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 2,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  delay: `${Math.random() * 8}s`,
  duration: `${6 + Math.random() * 6}s`,
  color: i % 3 === 0 ? '#6366f1' : i % 3 === 1 ? '#818cf8' : '#a5b4fc',
}));

/* ── Feature data ───────────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: '🔗',
    title: 'Instant Link Shortening',
    badge: 'free',
    desc: 'Paste any long URL and get a clean, short link in under a second. No sign-in needed to try — just paste, click, and share anywhere: email, chat, social media, or print.',
  },
  {
    icon: '📱',
    title: 'QR Code (Free for a limited time)',
    badge: 'free',
    desc: 'Every short link automatically gets a downloadable QR code. Drop it onto flyers, business cards, packaging, or presentations — and the underlying URL stays editable without reprinting anything.',
  },
  {
    icon: '🎯',
    title: 'Custom Aliases',
    badge: 'pro',
    desc: 'Replace random characters with meaningful words you choose — like rdrto.com/my-launch-event. Custom aliases make links easier to type, remember, and trust, improving click-through rates by up to 30%.',
  },
  /*   {
      icon: '📊',
      title: 'Real-Time Analytics',
      badge: 'pro',
      desc: "See every click as it happens. The dashboard shows you click counts, geographic breakdowns by country and city, device types (desktop, mobile, tablet), browsers used, and referrer sources — all in one view.",
    }, */
  {
    icon: '🔒',
    title: 'Password Protection',
    badge: 'pro',
    desc: 'Lock any link behind a password so only the right people can access the destination. Perfect for sharing draft content with clients, distributing team resources, or protecting sensitive materials during campaigns.',
  },
  {
    icon: '🏥',
    title: 'Self-Healing Links',
    badge: 'pro',
    desc: 'Your link automatically monitors its destination URL. When the primary URL goes down, it switches to a backup URL and alerts you — then switches back once the primary recovers. No downtime for your audience.',
  },
  {
    icon: '🔍',
    title: 'Link Intelligence Score',
    badge: 'business',
    desc: 'Get an instant 0–100 health score for your destination URLs. Graded across engagement, traffic trust, destination health, and compliance — so you always know if your links are performing at their best.',
  },
  /*   {
      icon: '⏱',
      title: 'Link Expiration Dates',
      badge: 'pro',
      desc: 'Set an exact expiry date and time for any link. Once expired, visitors see a friendly message instead of the destination — great for limited-time offers, event registrations, or time-sensitive promotions.',
    }, */
  {
    icon: '🏷️',
    title: 'Brand Name URLs',
    badge: 'business',
    desc: 'Embed your brand name directly into every short URL. Choose Brand Path (rdrto.com/yourbrand/code) or Brand Subdomain (yourbrand.rdrto.com/code) — both work out of the box with no DNS setup required.',
  },
];

/* ── Why items ──────────────────────────────────────────────────────── */
const WHY_ITEMS = [
  {
    icon: '⚡',
    title: 'Blazing Fast',
    desc: 'Redirects complete in under 50ms globally. Your audience never waits.',
  },
  {
    icon: '🧩',
    title: 'Ridiculously Simple',
    desc: 'Three clicks to a short link. No tutorials. No learning curve. Just results.',
  },
  {
    icon: '🛡',
    title: 'Secure by Design',
    desc: 'HTTPS everywhere, abuse detection, and optional passwords. Links you can trust.',
  },
  {
    icon: '📈',
    title: 'Data You Can Act On',
    desc: 'Not just counts — geography, devices, and referrers tell the full story of every click.',
  },
];

/* ── Animated counter hook ──────────────────────────────────────────── */
function useCountUp(target, duration = 1500, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const num = parseFloat(target.replace(/[^0-9.]/g, ''));
    const suffix = target.replace(/[0-9.]/g, '');
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
      setValue(parseFloat((eased * num).toFixed(1)) + suffix);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value || '0';
}

/* ── Trust Stat ─────────────────────────────────────────────────────── */
function TrustStat({ value, label, started }) {
  const animated = useCountUp(value, 1400, started);
  return (
    <div className="trust-stat">
      <div className="trust-stat-value">{started ? animated : '0'}</div>
      <div className="trust-stat-label">{label}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   HOME component
══════════════════════════════════════════════════════════════════════ */
const Home = () => {
  const trustRef = useRef(null);
  const [trustVisible, setTrustVisible] = useState(false);
  const [screenshotTab, setScreenshotTab] = useState(0);

  const SCREENSHOT_TABS = [
    { label: '📊 Analytics Dashboard', src: SCREENSHOT_ANALYTICS },
    { label: '🔍 Intelligence Score', src: SCREENSHOT_SCORE },
    { label: '🏥 Self-Healing Links', src: SCREENSHOT_HEALING },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTrustVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (trustRef.current) observer.observe(trustRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="rdrto-page">
      <Helmet>
        {/* Basic SEO */}
        <title>RDRTO | Secure URL Shortener with Self-Healing Links & Analytics</title>
        <meta name="description" content="RDRTO is a reliable URL shortener offering custom links, detailed analytics, link intelligence scores, and self-healing routing. Start shortening today for free." />
        <link rel="canonical" href="https://rdrto.com/" />
        {/* For the main landing page, indexing and following links is desired to rank on Google */}
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rdrto.com/" />
        <meta property="og:title" content="RDRTO | Secure URL Shortener with Self-Healing Links & Analytics" />
        <meta property="og:description" content="RDRTO is a reliable URL shortener offering custom links, detailed analytics, link intelligence scores, and self-healing routing. Start shortening today for free." />
        <meta property="og:image" content="https://rdrto.com/og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://rdrto.com/" />
        <meta property="twitter:title" content="RDRTO | Secure URL Shortener with Self-Healing Links & Analytics" />
        <meta property="twitter:description" content="RDRTO is a reliable URL shortener offering custom links, detailed analytics, link intelligence scores, and self-healing routing. Start shortening today for free." />
        <meta property="twitter:image" content="https://rdrto.com/og-image.jpg" />

        {/* JSON-LD markup generated by Google Structured Data Markup Helper. */}
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "SoftwareApplication",
              "name": "RDRTO",
              "image": "https://rdrto.com/logo.c1954bc3.png",
              "url": "https://rdrto.com/",
              "author": {
                "@type": "Person",
                "name": "Tim Kwok"
              },
              "applicationCategory": "UtilitiesApplication",
              "downloadUrl": "https://rdrto.com/",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0.00",
                "priceCurrency": "USD"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "125"
              }
            }
          `}
        </script>
      </Helmet>
      <style>{STYLES}</style>

      {/* ─── SCROLL PROGRESS BAR ────────────────────────────────────── */}
      <section className="hero">
        {/* Floating particles */}
        <div className="hero-canvas" aria-hidden="true">
          {PARTICLES.map(p => (
            <div
              key={p.id}
              className="hero-particle"
              style={{
                width: p.size, height: p.size,
                left: p.left, top: p.top,
                background: p.color,
                animationDelay: p.delay,
                animationDuration: p.duration,
                boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
              }}
            />
          ))}
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Smart Link Management Platform
          </div>

          <h1 className="hero-brand">RDRTO</h1>

          <p className="hero-tagline-main">
            <strong>R</strong>e<strong>D</strong>i<strong>R</strong>ect<strong>TO</strong> — the smarter way to share any link.
          </p>

          <p className="hero-description">
            Turn any long, messy URL into a clean, trackable short link in seconds.
            Share confidently, measure every click, and grow faster — all from one simple dashboard.
          </p>

          <div className="hero-ctas">
            <a href="/signup" className="btn-primary">
              🚀 Get Started Free
            </a>
            <a href="/login" className="btn-secondary">
              Sign In →
            </a>
          </div>
        </div>

        <div className="hero-scroll-hint" aria-hidden="true">
          <span>Scroll to explore</span>
          <div className="scroll-arrow" />
        </div>
      </section>

      {/* ─── TRUST BAR ─────────────────────────────────────────────── */}
      <div className="trust-bar" ref={trustRef}>
        <div className="trust-bar-inner">
          <TrustStat value="50ms" label="Avg Redirect Speed" started={trustVisible} />
          <div className="trust-divider" />
          <TrustStat value="99.9%" label="Uptime" started={trustVisible} />
          <div className="trust-divider" />
          <TrustStat value="6+" label="Smart Features" started={trustVisible} />
          <div className="trust-divider" />
          <TrustStat value="3" label="Flexible Plans" started={trustVisible} />
          <div className="trust-divider" />
          <div className="trust-stat">
            <div className="trust-stat-value" style={{ color: '#4ade80' }}>FREE</div>
            <div className="trust-stat-label">To Get Started</div>
          </div>
        </div>
      </div>

      {/* ─── BRAND DECODER ─────────────────────────────────────────── */}
      <div className="brand-section">
        <div className="brand-inner">
          <div className="brand-letters">
            {[
              { char: 'R', expand: <><strong>Re</strong>direct</> },
              { char: 'D', expand: <>starts with <strong>D</strong>irection</> },
              { char: 'R', expand: <><strong>R</strong>outing every link</> },
              { char: 'T', expand: <><strong>T</strong>racking every click</> },
              { char: 'O', expand: <>always <strong>O</strong>n target</> },
            ].map((row, i) => (
              <div
                key={i}
                className="brand-letter-row"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <span className="brand-letter-char">{row.char}</span>
                <span className="brand-letter-expand">{row.expand}</span>
              </div>
            ))}
          </div>

          <div className="brand-right">
            <span className="section-label">What is RDRTO?</span>
            <h2 className="section-title">
              The name that says<br />
              exactly <span>what it does</span>
            </h2>
            <p className="brand-intro-text">
              <strong>RDRTO</strong> stands for <strong>ReDiRect TO</strong> — and that's precisely
              what it does: it redirects anyone who clicks your short link straight to where
              you want them to go. Fast, reliable, and free to start.
            </p>
            <p className="brand-intro-text" style={{ marginTop: 16 }}>
              Whether you're a marketer tracking campaign performance, a blogger sharing articles,
              or a business distributing resources — RDRTO gives you a single place to
              <strong> create, manage, and measure</strong> every link you share.
              No complexity. No clutter. Just smart redirects.
            </p>
            <div style={{ marginTop: 32 }}>
              <a href="/signup" className="btn-primary" style={{ fontSize: '0.95rem', padding: '14px 28px' }}>
                Try it free — no card needed
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SCREENSHOT GALLERY ─────────────────────────────────────── */}
      <div className="screenshots-section">
        <div className="screenshots-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-label">Product Preview</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              See it in <span>action</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              A powerful dashboard designed for clarity — every metric you need, exactly where you expect it.
            </p>
          </div>
          <div className="screenshots-tabs">
            {SCREENSHOT_TABS.map((t, i) => (
              <button
                key={i}
                className={`screenshot-tab${screenshotTab === i ? ' active' : ''}`}
                onClick={() => setScreenshotTab(i)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="screenshot-frame" key={screenshotTab}>
            <img src={SCREENSHOT_TABS[screenshotTab].src} alt={SCREENSHOT_TABS[screenshotTab].label} loading="lazy" />
          </div>
        </div>
      </div>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#0a0f1e' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            <span className="section-label">How It Works</span>
            <h2 className="section-title" style={{ margin: '0 auto', textAlign: 'center' }}>
              Three steps — <span>that's all it takes</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              From long URL to trackable short link in under 30 seconds.
            </p>
          </div>

          <div className="steps-grid" style={{ marginTop: 60 }}>
            {[
              {
                n: '01', icon: '📋', title: 'Paste Your URL',
                desc: 'Copy any long link from your browser, document, or email, and paste it into RDRTO. Any URL works — no restrictions on destination.',
                delay: '0s',
              },
              null,
              {
                n: '02', icon: '✂️', title: 'Create Your Link',
                desc: 'Click "Shorten" and your link is ready instantly. With a Pro plan, add a custom alias so the link says what it points to.',
                delay: '0.15s',
              },
              null,
              {
                n: '03', icon: '📡', title: 'Share & Track',
                desc: 'Share your short link anywhere. Watch click data roll in on your analytics dashboard — where, when, and how people click.',
                delay: '0.3s',
              },
            ].map((item, i) => {
              if (!item) {
                return (
                  <div className="step-connector" key={i}>
                    <span className="connector-arrow">→</span>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  className="step-card"
                  style={{ animationDelay: item.delay }}
                >
                  <div className="step-number">{item.n}</div>
                  <span className="step-icon">{item.icon}</span>
                  <div className="step-title">{item.title}</div>
                  <p className="step-desc">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURES GRID ─────────────────────────────────────────── */}
      <div className="features-bg">
        <div className="features-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-label">Features</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Everything you need to <span>share smarter</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              Start for free. Unlock advanced tools with Pro when you're ready to scale.
            </p>
          </div>

          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="feature-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="feature-card-header">
                  <div className="feature-icon-wrap">{f.icon}</div>
                  <span className={`feature-badge badge-${f.badge}`}>
                    {f.badge.toUpperCase()}
                  </span>
                </div>
                <div className="feature-card-title">{f.title}</div>
                <p className="feature-card-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── SIGNATURE FEATURES SPOTLIGHT ─────────────────────────── */}
      <div className="spotlight-section">
        <div className="spotlight-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-label">Only on RDRTO</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Features you won't find <span>anywhere else</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              Two capabilities built from the ground up to keep your links healthy and trustworthy — automatically.
            </p>
          </div>

          <div className="spotlight-grid">
            {/* Link Intelligence Score */}
            <div className="spotlight-card spotlight-card-score">
              <div className="spotlight-tag spotlight-tag-score">🔍 Business Feature</div>
              <h3 className="spotlight-title">Link Intelligence Score</h3>
              <p className="spotlight-desc">
                A 0–100 composite health score calculated automatically for every link. Know at a glance whether your destination is fast, safe, and trusted — before your audience notices a problem.
              </p>
              <ul className="spotlight-bullets">
                {[
                  ['📈', 'Engagement score — click trends, volume, unique visitor ratio'],
                  ['🛡️', 'Traffic Trust — bot detection, IP diversity, velocity anomaly'],
                  ['🌐', 'Destination Health — HTTP status, SSL, and response speed'],
                  ['✅', 'Compliance — abuse flags, activation status, link lifespan'],
                ].map(([icon, text], i) => (
                  <li key={i}>
                    <span className="spotlight-bullet-dot dot-score">{icon}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Self-Healing Links */}
            <div className="spotlight-card spotlight-card-heal">
              <div className="spotlight-tag spotlight-tag-heal">🏥 Pro / Business Feature</div>
              <h3 className="spotlight-title">Self-Healing Links</h3>
              <p className="spotlight-desc">
                Your short link watches its own destination. When the primary URL goes down, it switches to a backup URL and alerts you by email — then automatically switches back once it's restored.
              </p>
              <ul className="spotlight-bullets">
                {[
                  ['🔄', 'Automatically detects broken destination URLs on a schedule'],
                  ['🔀', 'Switches to backup URLs instantly — visitors never see an error'],
                  ['📧', 'Email alerts when healing starts and when primary is restored'],
                  ['🟢', 'Auto-recovers back to primary when it comes back online'],
                ].map(([icon, text], i) => (
                  <li key={i}>
                    <span className="spotlight-bullet-dot dot-heal">{icon}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>


      <section style={{ padding: '100px 24px', background: '#0a0f1e' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <span className="section-label">Why RDRTO</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Built for people who value <span>clarity and speed</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              No bloat. No noise. Just the tools that actually help you.
            </p>
          </div>

          <div className="why-grid">
            {WHY_ITEMS.map((w, i) => (
              <div
                key={i}
                className="why-card"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <span className="why-icon">{w.icon}</span>
                <div className="why-title">{w.title}</div>
                <p className="why-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA BANNER ────────────────────────────────────────────── */}
      <div style={{ padding: '0 24px 100px' }}>
        <div className="cta-banner">
          <h2 className="cta-banner-title">
            Start redirecting smarter — free forever
          </h2>
          <p className="cta-banner-sub">
            No credit card. No setup fee. 20 free links to get you started in seconds.
          </p>
          <div className="cta-banner-actions">
            <a href="/signup" className="btn-primary" style={{ fontSize: '1.05rem', padding: '18px 44px' }}>
              Create Your Free Account
            </a>
            <a href="/upgrade" className="btn-secondary" style={{ fontSize: '1.05rem', padding: '18px 44px' }}>
              See Pro / Business Features →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
